import React from 'react';

import { client } from '~/api/client';
import {
  AmsLogStatus,
  type OutboundEmailAmsLogFieldsFragment,
  useSentEmailAmsLogLazyQuery
} from '~/api/graphql';
import { amsLogPollingRemainingMs } from '~/components/ams-email-log/polling';

export const SENT_EMAIL_POLL_MS = 5_000;

type SentEmailAmsLogState = {
  key: string;
  amsLog: OutboundEmailAmsLogFieldsFragment | null;
};

/** Polls a new send briefly, then checks an unsettled log when the window returns. */
export const useSentEmailAmsLog = (args: {
  enabled: boolean;
  artifactId: string;
  sentEmailId: string | undefined;
  sentAt: Date | string | null | undefined;
}) => {
  const { enabled, artifactId, sentEmailId, sentAt } = args;
  const key = JSON.stringify([artifactId, sentEmailId]);
  const [answer, setAnswer] = React.useState<SentEmailAmsLogState>();
  const [readRequest, setReadRequest] = React.useState(0);
  const [expiredKey, setExpiredKey] = React.useState<string>();
  const shownKey = React.useRef<string>();
  const activeKey = React.useRef(key);
  activeKey.current = key;
  const isUnsettled = React.useRef(true);
  const [read, { data, error, refetch, startPolling, stopPolling }] = useSentEmailAmsLogLazyQuery({
    client,
    fetchPolicy: 'no-cache',
    context: { queryDeduplication: false },
    notifyOnNetworkStatusChange: true
  });

  React.useEffect(() => {
    if (!enabled || !sentEmailId) return;
    isUnsettled.current = true;
    setExpiredKey(undefined);
    void read({ variables: { workspaceArtifactId: artifactId } }).catch(() => undefined);
    const remainingMs = amsLogPollingRemainingMs(AmsLogStatus.Logging, sentAt, Date.now());
    if (remainingMs <= 0) {
      setExpiredKey(key);
      stopPolling();
      return;
    }
    startPolling(SENT_EMAIL_POLL_MS);
    const timer = window.setTimeout(() => {
      if (!isUnsettled.current) return;
      stopPolling();
      setExpiredKey(key);
      void refetch().catch(() => undefined);
    }, remainingMs);
    return () => {
      window.clearTimeout(timer);
      stopPolling();
    };
  }, [
    enabled,
    artifactId,
    sentEmailId,
    sentAt,
    key,
    readRequest,
    read,
    refetch,
    startPolling,
    stopPolling
  ]);

  React.useEffect(() => {
    if (!enabled || error || !data || shownKey.current === key) return;
    const sent = data.searchOutboundEmails.edges.find((edge) => edge.node.id === sentEmailId);
    if (!sent) return;
    const amsLog = sent.node.amsLog ?? null;
    setAnswer({ key, amsLog });
    isUnsettled.current = amsLog?.status === AmsLogStatus.Logging;
    if (!isUnsettled.current) {
      stopPolling();
      setExpiredKey(undefined);
    }
  }, [data, error, enabled, key, sentEmailId, stopPolling]);

  React.useEffect(() => {
    if (!enabled || expiredKey !== key) return;
    const onReturn = () => {
      if (document.visibilityState !== 'hidden' && isUnsettled.current) {
        void refetch().catch(() => undefined);
      }
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [enabled, expiredKey, key, refetch]);

  const showAmsLog = React.useCallback(
    (amsLog: OutboundEmailAmsLogFieldsFragment) => {
      if (!sentEmailId || activeKey.current !== key) return;
      setExpiredKey(undefined);
      shownKey.current = key;
      isUnsettled.current = false;
      stopPolling();
      setAnswer({ key, amsLog });
    },
    [key, sentEmailId, stopPolling]
  );
  const readAgain = React.useCallback(() => {
    shownKey.current = undefined;
    setReadRequest((count) => count + 1);
  }, []);

  const currentAnswer = enabled && answer?.key === key ? answer : undefined;
  return {
    amsLog: currentAnswer?.amsLog ?? null,
    hasFailed: enabled && expiredKey === key && !currentAnswer,
    showAmsLog,
    readAgain
  };
};
